import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import dayjs from 'dayjs'
import { Clock, History } from 'lucide-react'
import type { JSONContent } from '@tiptap/react'
import type { ProjectNoteEntry } from '@/redux/service/project'
import { extractPlainTextPreview } from '@/lib/tiptapContent'
import RichTextEditor from './RichTextEditor'

const formatTimestamp = (iso: string) => {
  const d = dayjs(iso)
  return d.isValid() ? d.format('MMM D, YYYY [at] h:mm:ss a') : ''
}

const initials = (name: string) =>
  name
    ?.split(' ')
    .filter(Boolean)
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

interface NotesHistoryListProps {
  history: ProjectNoteEntry[]
}

const NotesHistoryList: React.FC<NotesHistoryListProps> = ({ history }) => {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  if (history.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center gap-2 py-10 text-center'>
        <History className='h-7 w-7 text-muted-foreground/30' />
        <p className='text-xs text-muted-foreground'>
          No notes yet for this project.
        </p>
        <p className='text-[11px] text-muted-foreground/60'>
          Add the first one above.
        </p>
      </div>
    )
  }

  return (
    <ul className='divide-y divide-border/50'>
      {history.map((entry, idx) => {
        const isLatest = idx === 0
        const isExpanded = expandedId === entry.id
        return (
          <li key={entry.id}>
            <button
              type='button'
              onClick={() => setExpandedId(isExpanded ? null : entry.id)}
              className='w-full px-3 py-3 text-left transition-colors hover:bg-accent/30'
            >
              <div className='flex items-start gap-2.5'>
                <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-[10px] font-bold text-primary'>
                  {initials(entry.created_by_name)}
                </div>
                <div className='min-w-0 flex-1'>
                  <div className='flex flex-wrap items-center gap-1.5'>
                    <span className='truncate text-xs font-semibold'>
                      {entry.created_by_name}
                    </span>
                    {isLatest && (
                      <span className='inline-flex items-center rounded-md border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400'>
                        Latest
                      </span>
                    )}
                  </div>
                  <div
                    className='mt-0.5 truncate text-[10px] text-muted-foreground/70'
                    title={entry.created_by_email}
                  >
                    {entry.created_by_email}
                  </div>
                  <div className='mt-1 flex items-center gap-1 text-[10px] text-muted-foreground'>
                    <Clock className='h-2.5 w-2.5 shrink-0' />
                    {formatTimestamp(entry.created_at)}
                  </div>
                  {!isExpanded && (
                    <p className='mt-1.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground'>
                      {extractPlainTextPreview(entry.content as JSONContent) ||
                        '(empty)'}
                    </p>
                  )}
                </div>
              </div>
            </button>
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className='overflow-hidden px-3'
                >
                  <div className='pb-3'>
                    <RichTextEditor
                      content={entry.content as JSONContent}
                      editable={false}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        )
      })}
    </ul>
  )
}

export default NotesHistoryList
